import { Component, EventEmitter, Output, ViewContainerRef } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  _AbcButtonComponent,
  AbcContainerService,
  AbcWindowComponent,
  IWindow,
  SelectedMedia,
  WindowStyle,
} from '@barthauer/abc/ui-components';
import { CameraPreviewComponent } from '../camera-preview/camera-preview.component';
import { ConvertHelper, CordovaService, DataUrl, FileHelper } from '@barthauer/abc/core';
import { MediaSelectionComponent } from '../media-selection/media-selection.component';

@Component({
  selector: 'lib-source-selection',
  standalone: true,
  imports: [MatIconModule, FlexLayoutModule, _AbcButtonComponent],
  templateUrl: './source-selection.component.html',
  styleUrl: './source-selection.component.scss',
})
export class SourceSelectionComponent {
  @Output() onMediaSelected = new EventEmitter<SelectedMedia[]>();
  @Output() onCloseDemand = new EventEmitter();

  protected res: ResourceSet;
  protected CordovaService = CordovaService;
  constructor(
    private resourceManagerService: ResourceManagerService,
    private abcContainer: AbcContainerService,
    private cordovaService: CordovaService,
  ) {}

  async ngOnInit() {
    this.res = await this.resourceManagerService.getResourceSet('abc-media-gallery');
  }

  async onCameraButtonClick(): Promise<void> {
    if (CordovaService.isCordovaEnvironmentDetected) {
      const dataUrl = await this.cordovaService.takePicture();
      const media: SelectedMedia = new SelectedMedia();
      media.dataUrl = dataUrl;
      this.onMediaSelected.emit([media]);
    } else {
      this.captureFromBrowserCamera();
    }
    this.onCloseDemand.emit();
  }

  private captureFromBrowserCamera(): void {
    const style: WindowStyle = {
      height: '500px',
      width: '450px',
      shading: true,
      resizable: true,
      position: { my: 'center center', at: 'center center', offset: '0 0' },
    };

    const windowAttr: IWindow = {
      id: 'cameraPreviewWindow',
      label: this.res?.get('CameraPreview_Title'),
      maximizeButton: false,
      minimizeButton: false,
      component: CameraPreviewComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    let abcWindow: ViewContainerRef;
    const window = this.abcContainer.createComponent(abcWindow, AbcWindowComponent, windowAttr);
    const windowInstance: AbcWindowComponent = <AbcWindowComponent>window.componentRef.instance;

    windowInstance.createComponentInstance().subscribe((windowComponent) => {
      const w = windowComponent.openWindow();
      windowInstance.onAfterOpened.subscribe(() => {
        const cameraPreviewInstance: CameraPreviewComponent = w.instance as CameraPreviewComponent;

        cameraPreviewInstance.onCapture.subscribe((dataUrl: DataUrl) => {
          const media: SelectedMedia = new SelectedMedia();
          media.dataUrl = dataUrl;
          this.onMediaSelected.emit([media]);
          this.onCloseDemand.emit();
          windowInstance.closeDialog();
        });
      });
      windowInstance.onAfterClosed.subscribe(() => window.componentRef.destroy());
    });
  }

  async onFilesOrGalleryButtonClick(): Promise<void> {
    if (CordovaService.isCordovaEnvironmentDetected) {
      const files: FileList = await this.pickFromGallery();
      const medias = await Promise.all(Array.from(files).map(async (file) => await this.fileToMedia(file)));
      this.onMediaSelected.emit(medias);
    } else {
      this.openMediaSelection();
    }
    this.onCloseDemand.emit();
  }

  private openMediaSelection(): void {
    const style: WindowStyle = {
      height: '165px',
      width: '380px',
      shading: true,
      position: { my: 'center center', at: 'center center', offset: '0 0' },
    };

    const WindowAttr: IWindow = {
      id: 'mediaSelectionWindow',
      label: this.res?.get('MediaSelection_Title'),
      maximizeButton: false,
      minimizeButton: false,
      component: MediaSelectionComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    let abcWindow: ViewContainerRef;
    const window = this.abcContainer.createComponent(abcWindow, AbcWindowComponent, WindowAttr);
    const windowInstance: AbcWindowComponent = <AbcWindowComponent>window.componentRef.instance;

    windowInstance.createComponentInstance().subscribe((windowComponent) => {
      const mediaSelectionInstance: MediaSelectionComponent = <MediaSelectionComponent>(
        windowComponent.openWindow().instance
      );
      mediaSelectionInstance.onFilesSelected.subscribe(async (selectedFiles: File[]) => {
        const medias = await Promise.all(selectedFiles.map(async (file) => await this.fileToMedia(file)));
        this.onMediaSelected.emit(medias);

        this.onCloseDemand.emit();
        windowInstance.closeDialog();
      });

      mediaSelectionInstance.onCloseDemand.subscribe(() => windowInstance.closeDialog());
      windowInstance.onAfterClosed.subscribe(() => window.componentRef.destroy());
    });
  }

  private async fileToMedia(file: File): Promise<SelectedMedia> {
    const media = new SelectedMedia();
    if (this.isMinorImage(file)) {
      media.dataUrl = await ConvertHelper.readFileAsDataUrl(file);
    } else {
      media.file = file;
    }
    return media;
  }

  private isMinorImage(file: File): boolean {
    const extension = FileHelper.getExtension(file.name);
    const sizeMB = file.size / (1024 * 1024); // Bytes → MB

    return (extension === 'jpg' || extension === 'jpeg' || extension === 'png') && sizeMB <= 5;
  }

  private pickFromGallery(): Promise<FileList> {
    const accept: string = 'video/*, image/*';
    return this.createHiddenPicker({
      accept: accept,
      multiple: true,
    });
  }

  private createHiddenPicker(opts: { accept: string; multiple: boolean }): Promise<FileList> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = opts.accept;
      input.multiple = opts.multiple;

      input.style.position = 'fixed';
      input.style.opacity = '0';
      input.style.pointerEvents = 'none';
      input.style.width = '0';
      input.style.height = '0';

      const cleanup = () => {
        input.removeEventListener('change', onChange);
        input.removeEventListener('cancel', onCancel as any);
        if (document.body.contains(input)) {
          document.body.removeChild(input);
        }
      };

      const onChange = () => {
        if (input.files && input.files.length > 0) {
          resolve(input.files);
        } else {
          reject('no files selected');
        }
        cleanup();
      };

      const onCancel = () => {
        reject('picker cancelled');
        cleanup();
      };

      input.addEventListener('change', onChange);
      // manche Browser triggern kein change bei Abbruch → optional:
      input.addEventListener('cancel', onCancel as any);

      document.body.appendChild(input);

      setTimeout(() => input.click(), 0);
    });
  }
}
